import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PlaylistAddRoundedIcon from "@mui/icons-material/PlaylistAddRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import { insetSx } from "../center/ownerCenterUi";
import { dateTimeLabel, minutesLabel } from "./ownerProjectUi";

const TYPE_LABELS = {
  action: "Acción",
  objective: "Objetivo",
  fix: "Fix",
  idea: "Idea",
  meeting: "Reunión",
  note: "Nota",
};

const FILTERS = [
  ["all", "Todos"],
  ["ready", "Listos"],
  ["waiting", "Esperando"],
  ["blocked", "Bloqueados"],
  ["objective", "Objetivos"],
  ["fix", "Fixes"],
];

let draftKey = 0;
function emptyDraft() {
  draftKey += 1;
  return { key: `step-${draftKey}`, title: "", estimatedMinutes: "" };
}

function stateOf(step) {
  const dependencyCode = String(step?.executability?.code || "");
  if (dependencyCode.startsWith("dependency_")) return "dependency";
  return String(step?.work?.state || (step?.projectRole === "blocker" ? "blocked" : "ready"));
}

function visibleType(step) {
  const state = stateOf(step);
  if (state === "waiting") return { label: "Espera", color: "warning" };
  if (state === "blocked") return { label: "Bloqueo", color: "error" };
  if (state === "dependency") return { label: "Depende", color: "warning" };
  return { label: TYPE_LABELS[step?.projectItemType || "action"] || "Acción", color: step?.projectItemType === "fix" ? "warning" : "default" };
}

function matchesFilter(step, filter) {
  if (filter === "all") return true;
  if (filter === "objective" || filter === "fix") return (step.projectItemType || "action") === filter;
  const state = stateOf(step);
  if (filter === "ready") return ["ready", "in_progress"].includes(state) && step?.executability?.executable !== false;
  return state === filter;
}

function metaLine(step) {
  const rows = [];
  if (step?.work?.reviewAt) rows.push(`Revisar ${dateTimeLabel(step.work.reviewAt)}`);
  if (step?.scheduledStartAt) rows.push(`Agenda ${dateTimeLabel(step.scheduledStartAt)}`);
  if (step?.estimatedMinutes) rows.push(`≈ ${minutesLabel(step.estimatedMinutes)}`);
  if (step?.dueAt) rows.push(`Límite ${dateTimeLabel(step.dueAt)}`);
  return rows.join(" · ");
}

function PendingRow({ step, number, index, total, moving, deleting, onMove, onEdit, onDelete, onDone, onState, onTime }) {
  const state = stateOf(step);
  const type = visibleType(step);
  const dependencyTitle = step?.executability?.dependency?.title || step?.dependency?.title || "";
  const detail = state === "waiting"
    ? step?.work?.waitingFor
    : state === "blocked"
      ? (step?.work?.blockedReason || step?.work?.blockedBy || "")
      : state === "dependency"
        ? dependencyTitle
        : step?.work?.definitionOfDone || "";
  const meta = metaLine(step);
  const contextOnly = ["objective", "idea", "note"].includes(step?.projectItemType || "action");
  const actionButtonSx = {
    width: { xs: 40, sm: 30 },
    height: { xs: 40, sm: 30 },
    borderRadius: { xs: 1.5, sm: "50%" },
    bgcolor: { xs: "action.hover", sm: "transparent" },
    "&:hover": { bgcolor: "action.hover" },
  };

  return <Box sx={{ ...insetSx, p: { xs: .85, sm: .9 }, overflow: "hidden" }}>
    <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: .7, sm: .8 }} alignItems={{ sm: "flex-start" }}>
      <Stack direction="row" spacing={.8} alignItems="flex-start" sx={{ minWidth: 0, flex: 1, width: "100%" }}>
        <Box sx={{ width: 27, height: 27, borderRadius: "50%", display: "grid", placeItems: "center", bgcolor: "action.hover", flexShrink: 0, mt: .05 }}>
          <Typography sx={{ fontWeight: 1000, fontSize: ".69rem" }}>{number}</Typography>
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontWeight: 950, fontSize: { xs: ".9rem", sm: ".84rem" }, lineHeight: 1.28, overflowWrap: "anywhere" }}>{step.title}</Typography>
          <Stack direction="row" spacing={.4} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: .4 }}>
            <Chip size="small" label={type.label} color={type.color} variant={type.color === "default" ? "outlined" : "filled"} sx={{ height: 21, fontSize: ".58rem", fontWeight: 900 }} />
            {state === "in_progress" ? <Chip size="small" color="primary" label="Haciendo" sx={{ height: 21, fontSize: ".58rem", fontWeight: 900 }} /> : null}
            {step.projectRole === "microtask" ? <Chip size="small" variant="outlined" label="Micro" sx={{ height: 21, fontSize: ".58rem" }} /> : null}
          </Stack>
          {detail ? <Typography sx={{ mt: .4, color: state === "blocked" ? "error.main" : state === "dependency" ? "warning.dark" : "text.secondary", fontSize: { xs: ".74rem", sm: ".7rem" }, lineHeight: 1.4, overflowWrap: "anywhere" }}>
            {state === "waiting" ? "Esperando: " : state === "blocked" ? "Bloquea: " : state === "dependency" ? "Primero falta: " : step?.projectItemType === "objective" ? "Se cumple cuando: " : "Listo cuando: "}{detail}
          </Typography> : null}
          {meta ? <Typography sx={{ mt: .28, color: "text.secondary", fontSize: { xs: ".69rem", sm: ".66rem" }, lineHeight: 1.35 }}>{meta}</Typography> : null}
        </Box>
      </Stack>

      <Box sx={{
        width: { xs: "100%", sm: "auto" },
        pt: { xs: .65, sm: 0 },
        borderTop: { xs: "1px solid", sm: "none" },
        borderColor: { xs: "divider", sm: "transparent" },
        flexShrink: 0,
      }}>
        <Stack direction="row" spacing={{ xs: .35, sm: .05 }} useFlexGap flexWrap="wrap" justifyContent={{ xs: "flex-end", sm: "flex-start" }}>
          {!contextOnly && state === "ready" && step?.executability?.executable !== false ? <Tooltip title="Empezar"><IconButton aria-label="Empezar pendiente" size="small" onClick={() => onState?.(step, "in_progress")} sx={actionButtonSx}><PlayArrowRoundedIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip> : null}
          {!contextOnly && state === "waiting" ? <Tooltip title="Llegó / retomar"><IconButton aria-label="Retomar pendiente" size="small" onClick={() => onState?.(step, "ready")} sx={actionButtonSx}><PlayArrowRoundedIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip> : null}
          <Tooltip title="Marcar hecho"><IconButton aria-label="Marcar pendiente como hecho" size="small" onClick={() => onDone?.(step)} sx={actionButtonSx}><CheckRoundedIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
          {!contextOnly ? <Tooltip title="Registrar tiempo"><IconButton aria-label="Registrar tiempo" size="small" onClick={() => onTime?.(step)} sx={actionButtonSx}><AccessTimeRoundedIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip> : null}
          <Tooltip title="Editar"><IconButton aria-label="Editar pendiente" size="small" onClick={() => onEdit?.(step)} sx={actionButtonSx}><EditRoundedIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
          <Tooltip title="Subir"><span><IconButton aria-label="Subir pendiente" size="small" disabled={moving || index === 0} onClick={() => onMove(index, -1)} sx={actionButtonSx}><KeyboardArrowUpRoundedIcon sx={{ fontSize: 18 }} /></IconButton></span></Tooltip>
          <Tooltip title="Bajar"><span><IconButton aria-label="Bajar pendiente" size="small" disabled={moving || index >= total - 1} onClick={() => onMove(index, 1)} sx={actionButtonSx}><KeyboardArrowDownRoundedIcon sx={{ fontSize: 18 }} /></IconButton></span></Tooltip>
          <Tooltip title="Sacar del mapa"><span><IconButton aria-label="Eliminar pendiente" color="error" size="small" disabled={deleting} onClick={() => onDelete?.(step, number)} sx={actionButtonSx}><DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} /></IconButton></span></Tooltip>
        </Stack>
      </Box>
    </Stack>
  </Box>;
}

function DoneRow({ step }) {
  const type = visibleType(step);
  return <Box sx={{ px: .85, py: .7, borderBottom: "1px solid", borderColor: "divider" }}>
    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={.45}>
      <Stack direction="row" spacing={.55} alignItems="center" useFlexGap flexWrap="wrap">
        <Chip size="small" color="success" label="Hecho" sx={{ height: 20, fontSize: ".57rem", fontWeight: 900 }} />
        <Chip size="small" variant="outlined" label={type.label} sx={{ height: 20, fontSize: ".57rem" }} />
        <Typography sx={{ fontWeight: 850, fontSize: ".78rem" }}>{step.title}</Typography>
      </Stack>
      <Typography sx={{ color: "text.secondary", fontSize: ".65rem", whiteSpace: "nowrap" }}>{step.completedAt ? dateTimeLabel(step.completedAt) : "Histórico"}</Typography>
    </Stack>
  </Box>;
}

export default function OwnerProjectStepsPanel({ steps, onCreateSteps, onReorderSteps, onDeleteStep, onEdit, onDone, onState, onTime, onNewActivity }) {
  const items = steps?.items || [];
  const pending = items.filter((row) => row.status === "open");
  const done = items.filter((row) => row.status === "done").slice().reverse();
  const [view, setView] = React.useState("pending");
  const [filter, setFilter] = React.useState("all");
  const [historyLimit, setHistoryLimit] = React.useState(30);
  const [adding, setAdding] = React.useState(false);
  const [drafts, setDrafts] = React.useState(() => [emptyDraft(), emptyDraft()]);
  const [saving, setSaving] = React.useState(false);
  const [moving, setMoving] = React.useState(false);
  const [deletingId, setDeletingId] = React.useState("");
  const [error, setError] = React.useState("");

  const filteredPending = pending.filter((row) => matchesFilter(row, filter));
  const updateDraft = (key, field, value) => setDrafts((prev) => prev.map((row) => row.key === key ? { ...row, [field]: value } : row));
  const addDraft = () => setDrafts((prev) => [...prev, emptyDraft()]);
  const removeDraft = (key) => setDrafts((prev) => prev.length <= 1 ? prev : prev.filter((row) => row.key !== key));

  const saveDrafts = async () => {
    const rows = drafts
      .map((row) => ({ title: String(row.title || "").trim(), estimatedMinutes: row.estimatedMinutes === "" ? null : Number(row.estimatedMinutes) }))
      .filter((row) => row.title);
    if (!rows.length) { setError("Escribí al menos una acción."); return; }
    const invalidDuration = rows.find((row) => row.estimatedMinutes !== null && (!Number.isFinite(row.estimatedMinutes) || row.estimatedMinutes < 5 || row.estimatedMinutes > 1440));
    if (invalidDuration) { setError("Las duraciones deben estar entre 5 minutos y 24 horas."); return; }
    setSaving(true); setError("");
    try {
      await onCreateSteps?.(rows);
      setDrafts([emptyDraft(), emptyDraft()]);
      setAdding(false);
    } catch (err) {
      setError(err?.message || "No pude agregar las acciones.");
    } finally {
      setSaving(false);
    }
  };

  const move = async (index, delta) => {
    const nextIndex = index + delta;
    if (nextIndex < 0 || nextIndex >= pending.length || moving) return;
    const firstId = pending[index]?.id;
    const secondId = pending[nextIndex]?.id;
    const ordered = items.filter((row) => row.status !== "cancelled").map((row) => row.id);
    const firstPos = ordered.indexOf(firstId);
    const secondPos = ordered.indexOf(secondId);
    if (firstPos < 0 || secondPos < 0) return;
    [ordered[firstPos], ordered[secondPos]] = [ordered[secondPos], ordered[firstPos]];
    setMoving(true); setError("");
    try { await onReorderSteps?.(ordered); }
    catch (err) { setError(err?.message || "No pude guardar el nuevo orden."); }
    finally { setMoving(false); }
  };

  const deleteStep = async (step, number) => {
    if (!step?.id || deletingId) return;
    const confirmed = window.confirm(`¿Sacar el pendiente ${number} del mapa activo?\n\n${step.title}\n\nEl historial se conserva.`);
    if (!confirmed) return;
    setDeletingId(step.id); setError("");
    try { await onDeleteStep?.(step); }
    catch (err) { setError(err?.message || "No pude sacar ese pendiente."); }
    finally { setDeletingId(""); }
  };

  const counts = {
    ready: pending.filter((row) => matchesFilter(row, "ready")).length,
    waiting: pending.filter((row) => stateOf(row) === "waiting").length,
    blocked: pending.filter((row) => stateOf(row) === "blocked").length,
  };

  return <Box sx={{ mt: .2 }}>
    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={.8} alignItems={{ sm: "center" }}>
      <Box>
        <Typography sx={{ fontWeight: 1000, fontSize: ".92rem" }}>Mapa del proyecto</Typography>
        <Typography sx={{ mt: .08, color: "text.secondary", fontSize: ".68rem" }}>{pending.length} pendientes · {counts.ready} listos · {counts.waiting} esperando · {counts.blocked} bloqueados</Typography>
      </Box>
      <Stack direction="row" spacing={.45} useFlexGap flexWrap="wrap">
        <Button size="small" variant="contained" startIcon={<AddRoundedIcon />} onClick={onNewActivity} sx={{ textTransform: "none", fontWeight: 950 }}>Anotar</Button>
        <Button size="small" variant={adding ? "text" : "outlined"} startIcon={<PlaylistAddRoundedIcon />} onClick={() => { setAdding((value) => !value); setError(""); }} sx={{ textTransform: "none", fontWeight: 900 }}>{adding ? "Cerrar" : "Varias acciones"}</Button>
      </Stack>
    </Stack>

    <Stack direction="row" spacing={.4} sx={{ mt: .8, p: .35, bgcolor: "action.hover", borderRadius: 2, width: "fit-content" }}>
      <Button size="small" variant={view === "pending" ? "contained" : "text"} onClick={() => setView("pending")} sx={{ minWidth: 0, px: 1.1, textTransform: "none", fontWeight: 950 }}>Pendientes {pending.length}</Button>
      <Button size="small" variant={view === "done" ? "contained" : "text"} onClick={() => setView("done")} sx={{ minWidth: 0, px: 1.1, textTransform: "none", fontWeight: 950 }}>Hechos {done.length}</Button>
    </Stack>

    {view === "pending" ? <>
      <Stack direction="row" spacing={.35} useFlexGap flexWrap="wrap" sx={{ mt: .65 }}>
        {FILTERS.map(([value, label]) => <Chip key={value} clickable size="small" label={label} color={filter === value ? "primary" : "default"} variant={filter === value ? "filled" : "outlined"} onClick={() => setFilter(value)} sx={{ fontWeight: 850 }} />)}
      </Stack>

      <Collapse in={adding} unmountOnExit>
        <Box sx={{ mt: .75, ...insetSx, p: 1 }}>
          <Typography sx={{ fontWeight: 950, fontSize: ".75rem" }}>Descarga rápida de acciones</Typography>
          <Typography sx={{ mt: .12, mb: .7, color: "text.secondary", fontSize: ".66rem" }}>Para objetivos, ideas, esperas o fixes usá “Anotar”; acá podés bajar varias acciones simples de una vez.</Typography>
          <Stack spacing={.6}>{drafts.map((row, index) => <Stack key={row.key} direction={{ xs: "column", sm: "row" }} spacing={.5} alignItems={{ sm: "center" }}>
            <TextField size="small" fullWidth label={`Acción ${index + 1}`} value={row.title} onChange={(event) => updateDraft(row.key, "title", event.target.value)} />
            <TextField size="small" type="number" label="Min" value={row.estimatedMinutes} onChange={(event) => updateDraft(row.key, "estimatedMinutes", event.target.value)} inputProps={{ min: 5, max: 1440, step: 5 }} sx={{ width: { xs: "100%", sm: 100 }, flexShrink: 0 }} />
            <IconButton size="small" disabled={drafts.length <= 1} onClick={() => removeDraft(row.key)}><DeleteOutlineRoundedIcon fontSize="small" /></IconButton>
          </Stack>)}</Stack>
          <Stack direction="row" justifyContent="space-between" sx={{ mt: .7 }}><Button size="small" onClick={addDraft} sx={{ textTransform: "none", fontWeight: 900 }}>Otra</Button><Button size="small" variant="contained" disabled={saving} onClick={saveDrafts} sx={{ textTransform: "none", fontWeight: 950 }}>{saving ? "Guardando…" : "Guardar"}</Button></Stack>
        </Box>
      </Collapse>

      {error ? <Alert severity="error" sx={{ mt: .65, py: 0 }}>{error}</Alert> : null}
      {filteredPending.length ? <Stack spacing={.45} sx={{ mt: .7 }}>{filteredPending.map((step) => {
        const originalIndex = pending.findIndex((row) => row.id === step.id);
        return <PendingRow key={step.id} step={step} number={originalIndex + 1} index={originalIndex} total={pending.length} moving={moving} deleting={deletingId === step.id} onMove={move} onEdit={onEdit} onDelete={deleteStep} onDone={onDone} onState={onState} onTime={onTime} />;
      })}</Stack> : <Box sx={{ ...insetSx, mt: .7, p: 1.1 }}><Typography sx={{ color: "text.secondary", fontSize: ".75rem" }}>No hay elementos en este filtro.</Typography></Box>}
    </> : <>
      <Typography sx={{ mt: .7, color: "text.secondary", fontSize: ".68rem" }}>Histórico del proyecto. No se mezcla con lo que todavía requiere atención.</Typography>
      {done.length ? <Box sx={{ mt: .6, border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>{done.slice(0, historyLimit).map((step) => <DoneRow key={step.id} step={step} />)}</Box> : <Box sx={{ ...insetSx, mt: .7, p: 1.1 }}><Typography sx={{ color: "text.secondary", fontSize: ".75rem" }}>Todavía no hay hechos cerrados.</Typography></Box>}
      {done.length > historyLimit ? <Button size="small" onClick={() => setHistoryLimit((value) => value + 30)} sx={{ mt: .5, textTransform: "none", fontWeight: 900 }}>Mostrar 30 más</Button> : null}
    </>}
  </Box>;
}
